import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../api/auth";

/* ── icons ── */
const MailIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="10" rx="2" stroke="#9ca3af" strokeWidth="1.5"/><path d="M1 6l7 4 7-4" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const PhoneIcon  = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 1h4l1.5 4-2 1.2a9.1 9.1 0 004.3 4.3L12 8.5 16 10v4a2 2 0 01-2 2A15 15 0 011 3a2 2 0 012-2z" stroke="#9ca3af" strokeWidth="1.5" strokeLinejoin="round"/></svg>;
const LockIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="8" rx="2" stroke="#9ca3af" strokeWidth="1.5"/><path d="M5 7V5a3 3 0 016 0v2" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const EyeIcon    = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="#9ca3af" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const EyeOffIcon = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M6.5 6.6A2 2 0 0010 10M4 4.3C2.4 5.4 1 8 1 8s2.5 5 7 5c1.4 0 2.7-.4 3.8-1M7 3.1C7.3 3 7.6 3 8 3c4.5 0 7 5 7 5s-.6 1.2-1.6 2.3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const AlertIcon  = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink:0 }}><circle cx="8" cy="8" r="7" stroke="#f87171" strokeWidth="1.5"/><path d="M8 5v4M8 11v.5" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const SpinIcon   = () => <span style={{ display:"inline-block", width:14, height:14, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"white", borderRadius:"50%", animation:"spin 0.6s linear infinite", marginRight:8, verticalAlign:"middle" }} />;

/* ── password strength ── */
const pwStrength = (p) => {
  if (!p) return { score:0, label:"", color:"#e0e0e0" };
  let score = 0;
  if (p.length >= 8)          score++;
  if (/[A-Z]/.test(p))        score++;
  if (/[0-9]/.test(p))        score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  const map = [
    { label:"", color:"#e0e0e0" },
    { label:"Faible",    color:"#f87171" },
    { label:"Moyen",     color:"#fbbf24" },
    { label:"Bon",       color:"#34d399" },
    { label:"Excellent", color:"#4ade80" },
  ];
  return { score, ...map[score] };
};

/* ── Step indicator ── */
const StepIndicator = ({ current }) => {
  const steps = ["Identité", "Nouveau mot de passe", "Confirmé"];
  return (
    <div style={{ display:"flex", alignItems:"flex-end", marginBottom:28 }}>
      {steps.map((label, i) => {
        const idx    = i + 1;
        const done   = idx < current;
        const active = idx === current;
        return (
          <React.Fragment key={label}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
              <div style={{
                width:30, height:30, borderRadius:"50%",
                border:`0.5px solid ${done ? "#4ade80" : active ? "#0f1117" : "#e0e0e0"}`,
                background: done ? "#4ade80" : active ? "#0f1117" : "transparent",
                color: done || active ? "white" : "#bbb",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:12, fontWeight:700,
              }}>
                {done ? "✓" : idx}
              </div>
              <div style={{ fontSize:11, color: active ? "#0f1117" : "#bbb", fontWeight: active ? 600 : 400, whiteSpace:"nowrap" }}>{label}</div>
            </div>
            {i < 2 && (
              <div style={{ flex:1, height:"0.5px", background: done ? "#4ade80" : "#e0e0e0", alignSelf:"center", marginBottom:18 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ── Panel Left ── */
const PanelLeft = ({ step }) => (
  <div style={s.panel} className="panel-left">
    <svg style={s.panelGrid} viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="gfp" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width="400" height="600" fill="url(#gfp)"/>
      <circle cx="60"  cy="500" r="200" fill="rgba(74,222,128,0.04)"/>
      <circle cx="340" cy="100" r="160" fill="rgba(74,222,128,0.05)"/>
      <circle cx="200" cy="300" r="80"  fill="none" stroke="rgba(74,222,128,0.08)" strokeWidth="0.5"/>
      <circle cx="200" cy="300" r="140" fill="none" stroke="rgba(74,222,128,0.05)" strokeWidth="0.5"/>
    </svg>

    <div style={s.panelContent}>
      <Link to="/" style={{ ...s.panelLogo, textDecoration:"none" }}>
        Trans<span style={{ color:"#4ade80" }}>Tigo</span>
      </Link>

      <div style={{ margin:"auto 0" }}>
        <h2 style={s.panelTitle}>
          {step === 1 ? "Récupérez l'accès à votre compte" : step === 2 ? "Choisissez un mot de passe fort" : "Compte récupéré !"}
        </h2>
        <p style={s.panelSub}>
          {step === 1
            ? "Entrez votre email et téléphone pour vérifier votre identité en toute sécurité."
            : step === 2
            ? "Votre identité a été confirmée. Définissez un nouveau mot de passe sécurisé."
            : "Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter."}
        </p>
      </div>

      {/* Security tips */}
      <div style={s.tipBox}>
        <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, color:"#4ade80", marginBottom:12 }}>
          Conseils sécurité
        </div>
        {[
          "Ne partagez jamais votre mot de passe",
          "Utilisez au moins 8 caractères",
          "Mélangez lettres, chiffres et symboles",
          "Évitez les mots du dictionnaire",
        ].map(tip => (
          <div key={tip} style={{ display:"flex", gap:10, marginBottom:8 }}>
            <span style={{ color:"#4ade80", flexShrink:0, fontSize:12 }}>→</span>
            <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)", lineHeight:1.5 }}>{tip}</span>
          </div>
        ))}
      </div>

      <div style={s.panelFooter}>
        <Link to="/login" style={{ fontSize:12, color:"rgba(255,255,255,0.35)", textDecoration:"none" }}>
          ← Retour à la connexion
        </Link>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════
   FORGET PASSWORD
══════════════════════════════════════════ */
const ForgetPassword = () => {
  const navigate = useNavigate();

  const [step,     setStep]     = useState(1);
  const [email,    setEmail]    = useState("");
  const [phone,    setPhone]    = useState("");
  const [token,    setToken]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [showCPwd, setShowCPwd] = useState(false);

  const strength = pwStrength(password);

  const handleVerify = async () => {
    setError("");
    if (!email || !phone) { setError("Veuillez remplir tous les champs."); return; }
    setLoading(true);
    try {
      const res = await axios.post(API.MDP + "/forgot-password", { email, phone });
      setToken(res.data.reset_token);
      setStep(2);
    } catch {
      setError("Informations incorrectes. Vérifiez votre email et téléphone.");
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setError("");
    if (password.length < 6) { setError("Le mot de passe doit contenir au moins 6 caractères."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      const res = await axios.post(API.MDP + "/reset-password", { token, password });
      localStorage.setItem("token", res.data.token);
      setStep(3);
      setTimeout(() => navigate("/dashboard"), 2500);
    } catch {
      setError("Une erreur est survenue. Veuillez recommencer.");
    }
    setLoading(false);
  };

  return (
    <div style={s.page}>
      <style>{globalCSS}</style>
      <PanelLeft step={step} />

      <div style={s.formSide}>
        <div style={s.formBox}>

          <div style={s.formHeader}>
            <h1 style={s.formTitle}>
              {step === 1 ? "Vérification d'identité" : step === 2 ? "Nouveau mot de passe" : "Tout est bon !"}
            </h1>
            <p style={s.formSub}>
              {step === 1 ? "Confirmez votre identité pour continuer." : step === 2 ? "Choisissez un mot de passe sécurisé." : "Redirection en cours…"}
            </p>
          </div>

          <StepIndicator current={step} />

          {error && (
            <div style={s.errorBox}><AlertIcon />{error}</div>
          )}

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              <div style={s.formField}>
                <label style={s.label}>Adresse email</label>
                <div style={{ position:"relative" }}>
                  <span style={s.inputIcon}><MailIcon /></span>
                  <input
                    style={{ ...s.input, paddingLeft:42 }}
                    type="email"
                    placeholder="vous@exemple.mg"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div style={{ ...s.formField, marginBottom:28 }}>
                <label style={s.label}>Numéro de téléphone</label>
                <div style={{ position:"relative" }}>
                  <span style={s.inputIcon}><PhoneIcon /></span>
                  <input
                    style={{ ...s.input, paddingLeft:42 }}
                    type="tel"
                    placeholder="+261 34 XXX XXX"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                </div>
              </div>

              <button style={s.submitBtn} onClick={handleVerify} disabled={loading} className="submit-btn">
                {loading ? <><SpinIcon />Vérification…</> : "Vérifier mon identité →"}
              </button>
            </>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <>
              <div style={s.formField}>
                <label style={s.label}>Nouveau mot de passe</label>
                <div style={{ position:"relative" }}>
                  <span style={s.inputIcon}><LockIcon /></span>
                  <input
                    style={{ ...s.input, paddingLeft:42, paddingRight:42 }}
                    type={showPwd ? "text" : "password"}
                    placeholder="Minimum 6 caractères"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button style={s.eyeBtn} type="button" onClick={() => setShowPwd(!showPwd)}>
                    {showPwd ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* strength bar */}
              {password && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", gap:4, marginBottom:4 }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{ flex:1, height:3, borderRadius:2, background: i <= strength.score ? strength.color : "#e8e8e4", transition:"background 0.2s" }} />
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:strength.color }}>{strength.label}</div>
                </div>
              )}

              <div style={{ ...s.formField, marginBottom:28 }}>
                <label style={s.label}>Confirmer le mot de passe</label>
                <div style={{ position:"relative" }}>
                  <span style={s.inputIcon}><LockIcon /></span>
                  <input
                    style={{
                      ...s.input, paddingLeft:42, paddingRight:42,
                      borderColor: confirm && confirm !== password ? "#f87171" : confirm && confirm === password ? "#4ade80" : undefined,
                    }}
                    type={showCPwd ? "text" : "password"}
                    placeholder="Répétez votre mot de passe"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                  />
                  <button style={s.eyeBtn} type="button" onClick={() => setShowCPwd(!showCPwd)}>
                    {showCPwd ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {confirm && confirm === password && (
                  <div style={{ fontSize:11, color:"#4ade80", marginTop:4 }}>✓ Mots de passe identiques</div>
                )}
              </div>

              <button style={s.submitBtn} onClick={handleReset} disabled={loading} className="submit-btn">
                {loading ? <><SpinIcon />Réinitialisation…</> : "Réinitialiser le mot de passe"}
              </button>
            </>
          )}

          {/* ── STEP 3 : success ── */}
          {step === 3 && (
            <div style={s.successBox}>
              <div style={s.successRing}>
                <div style={s.successCheck}>✓</div>
              </div>
              <h3 style={s.successTitle}>Mot de passe réinitialisé !</h3>
              <p style={s.successSub}>
                Votre mot de passe a été mis à jour avec succès. Vous allez être redirigé vers votre tableau de bord automatiquement.
              </p>
              <Link to="/dashboard" style={{ ...s.submitBtn, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", marginTop:8 }}>
                Accéder au tableau de bord
              </Link>
            </div>
          )}

          {step !== 3 && (
            <div style={{ textAlign:"center", marginTop:20 }}>
              <Link to="/login" style={{ fontSize:13, color:"#9ca3af", textDecoration:"none" }}>
                ← Retour à la connexion
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;

/* ── styles ── */
const s = {
  page:        { display:"flex", minHeight:"100vh", fontFamily:"'Plus Jakarta Sans', sans-serif" },
  panel:       { width:"42%", background:"#111318", position:"relative", overflow:"hidden", flexShrink:0 },
  panelGrid:   { position:"absolute", inset:0, width:"100%", height:"100%" },
  panelContent:{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", height:"100%", padding:"40px 44px" },
  panelLogo:   { fontSize:22, fontWeight:700, color:"white", letterSpacing:-0.5, display:"block" },
  panelTitle:  { fontSize:28, fontWeight:700, color:"white", letterSpacing:-1, lineHeight:1.2, marginBottom:12 },
  panelSub:    { fontSize:14, color:"rgba(255,255,255,0.4)", lineHeight:1.7 },
  tipBox:      { background:"rgba(255,255,255,0.03)", border:"0.5px solid rgba(255,255,255,0.06)", borderRadius:12, padding:"16px 18px", margin:"32px 0" },
  panelFooter: { marginTop:"auto", paddingTop:20, borderTop:"0.5px solid rgba(255,255,255,0.08)" },

  formSide:    { flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:"#fafaf8", padding:"40px 32px" },
  formBox:     { width:"100%", maxWidth:400 },
  formHeader:  { marginBottom:32 },
  formTitle:   { fontSize:26, fontWeight:700, letterSpacing:-0.8, color:"#0f1117", margin:"0 0 6px" },
  formSub:     { fontSize:13, color:"#9ca3af", margin:0 },

  errorBox:    { display:"flex", alignItems:"center", gap:8, background:"#fff5f5", border:"0.5px solid #fecaca", borderRadius:9, padding:"10px 14px", marginBottom:20, fontSize:13, color:"#dc2626" },
  formField:   { marginBottom:16 },
  label:       { display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:6, letterSpacing:0.2 },
  input:       { width:"100%", padding:"11px 14px", border:"0.5px solid #e0e0e0", borderRadius:10, fontSize:14, fontFamily:"inherit", color:"#0f1117", background:"white", outline:"none", boxSizing:"border-box", transition:"border-color 0.15s" },
  inputIcon:   { position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", display:"flex", alignItems:"center", pointerEvents:"none" },
  eyeBtn:      { position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:4, display:"flex", alignItems:"center" },
  submitBtn:   { width:"100%", padding:"13px 0", background:"#0f1117", color:"white", border:"none", borderRadius:10, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 },

  successBox:  { textAlign:"center", padding:"8px 0" },
  successRing: { width:72, height:72, borderRadius:"50%", border:"2px solid #4ade80", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", background:"#f0fdf4" },
  successCheck:{ fontSize:28, color:"#16a34a", fontWeight:700 },
  successTitle:{ fontSize:20, fontWeight:700, color:"#0f1117", marginBottom:10 },
  successSub:  { fontSize:13, color:"#6b7280", lineHeight:1.7, marginBottom:8 },
};

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  input:focus { border-color: #16a34a !important; box-shadow: 0 0 0 3px rgba(22,163,74,0.08) !important; }
  .submit-btn:hover:not(:disabled) { background: #1a1a1a !important; }
  .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  @media (max-width: 768px) { .panel-left { display: none !important; width: 0 !important; } }
`;